// RechargeCreditsModal — pops when the backend returns 402 "INSUFFICIENT_CREDITS"
// or when the user clicks the low-balance pill. Shows the shortfall, the
// task they were trying to run, and the available Super Credit packs.
// This is the "pause whatever task and ask user" gate the product spec
// asked for.
import React, { useEffect, useState } from 'react';
import { X, Wallet, AlertTriangle, Sparkles } from 'lucide-react';
import api from '../../studentApi';
import { friendlyError } from '../../../../lib/errors';
import { PackRowsSkeleton } from '../../StudentSkeletons';

export const PAID_NOT_CONFIRMED = 'Payment received. Your credits will appear in a few minutes. If they don’t, contact support.';

export default function RechargeCreditsModal({
  needed,           // number — credits needed for the paused task
  balance,          // number — current balance
  reason,           // string — short label of what the user was trying to do
  onClose,
  onTopUpComplete,  // optional: called with new balance after success
}) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyPlanId, setBusyPlanId] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get('/student/credits').then(d => d.plans || [])
      .then(rp => { if (mounted) setPlans(rp); })
      .catch(() => { if (mounted) setErr('Could not load credit packs.'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const buy = async (planId) => {
    setBusyPlanId(planId);
    setErr(null);
    try {
      const order = await api.post('/student/credits/super/order', { plan_id: planId, provider: 'razorpay' });
      // Test-mode auto-verify (no Razorpay keys present)
      if (order.test_mode) {
        const v = await api.post('/student/credits/super/verify', { order_id: order.id });
        if (onTopUpComplete) onTopUpComplete(v.super_credits_balance);
        onClose();
        return;
      }
      // Real Razorpay checkout — open the popup
      if (!window.Razorpay) {
        setErr('Payment SDK failed to load. Please refresh.');
        return;
      }
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount_inr * 100,
        currency: 'INR',
        name: 'Desk Super Credits',
        description: `${order.credits} super credits`,
        order_id: order.razorpay_order_id,
        handler: async (resp) => {
          try {
            const v = await api.post('/student/credits/super/verify', {
              order_id: order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            if (onTopUpComplete) onTopUpComplete(v.super_credits_balance);
            onClose();
          } catch {
            // The money is taken; the payment webhook still adds the credits.
            setErr(PAID_NOT_CONFIRMED);
          }
        },
        modal: { ondismiss: () => setBusyPlanId(null) },
        theme: { color: '#0EA5E9' },
      });
      rzp.open();
    } catch (e) {
      setErr(friendlyError(e, 'Could not start checkout.'));
    } finally {
      setBusyPlanId(null);
    }
  };

  const shortfall = Math.max(0, (needed || 0) - (balance || 0));

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.6)' }}
      data-testid="recharge-modal">
      <div
        className="w-full max-w-lg rounded-2xl shadow-lg overflow-hidden"
        style={{ background: 'var(--st-card, #fff)', color: 'var(--st-ink, #0f172a)' }}>
        {/* Header */}
        <div
          className="px-5 py-4 flex items-center gap-3"
          style={{ background: 'color-mix(in srgb, #F59E0B 12%, transparent)', borderBottom: '1px solid var(--st-border, #e5e7eb)' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: '#F59E0B' }}>
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-semibold leading-tight">You&apos;re out of super credits</div>
            <div className="text-xs opacity-70 mt-0.5">
              {reason
                ? <>Paused: <strong>{reason}</strong></>
                : <>Top up to keep going.</>}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="recharge-close"
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-black/5">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Stats row */}
        <div className="px-5 py-3 flex items-center justify-between gap-3 text-xs"
          style={{ borderBottom: '1px solid var(--st-border, #e5e7eb)' }}>
          <Stat label="Balance"  value={balance ?? 0} />
          <Stat label="Needed"   value={needed ?? 0} accent="#3B82F6" />
          <Stat label="Shortfall" value={shortfall} accent="#ef4444" emph />
        </div>

        {/* Plans */}
        <div className="px-5 pt-4 py-4 space-y-2 max-h-[50vh] overflow-auto">
          {loading && (
            <PackRowsSkeleton />
          )}
          {!loading && err && (
            <div className="text-center py-6 text-xs text-red-500">{err}</div>
          )}
          {!loading && !err && plans.map(p => (
            <button
              key={p.id}
              onClick={() => buy(p.id)}
              disabled={busyPlanId === p.id}
              data-testid={`recharge-pack-${p.id}`}
              className="w-full rounded-xl p-3 flex items-center gap-3 transition hover:shadow-md disabled:opacity-50 text-left"
              style={{
                background: 'var(--st-bg, #f8fafc)',
                border: '1.5px solid var(--st-border, #e5e7eb)',
              }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0"
                style={{ background: p.popular ? '#8B5CF6' : '#0EA5E9' }}>
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold leading-tight">{p.name}</div>
                <div className="text-xs opacity-70 mt-0.5">
                  {p.credits} credits{p.bonus_credits ? <> · <span className="text-emerald-600">+{p.bonus_credits} bonus</span></> : null}{p.popular ? <> · <span style={{ color: '#8B5CF6' }}>Most popular</span></> : null}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold">₹{p.price_inr}</div>
                <div className="text-xs opacity-60">≈ ${p.price_usd}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div
          className="px-5 py-3 flex items-center justify-between gap-2 text-xs opacity-70"
          style={{ borderTop: '1px solid var(--st-border, #e5e7eb)' }}>
          <span className="inline-flex items-center gap-1.5">
            <Wallet className="w-3 h-3" /> Secured by Razorpay
          </span>
          <button
            onClick={onClose}
            data-testid="recharge-cancel"
            className="px-2 py-1 rounded hover:bg-black/5 font-semibold">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, emph }) {
  return (
    <div className="flex-1 text-center">
      <div className="text-xs font-semibold opacity-60">{label}</div>
      <div className="text-[18px] font-semibold" style={{ color: accent || 'inherit', textDecoration: emph ? 'underline' : 'none' }}>
        {value}
      </div>
    </div>
  );
}
